// Administración del catálogo visual de Broadnet (script 911): marcas (nombre,
// color, logo) y montos/paquetes de cada producto con su imagen opcional.
// Solo nivel 5 (lo exige la base). Ni el color ni la imagen viajan a Broadnet:
// es presentación para el cajero.
import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { ImagePlus, Plus, Trash2 } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '@/components/ui/Button';
import { getErrorMessage } from '@/lib/api/client';
import { PanelIntegrado, useDialogoEnLinea } from '@/components/ui/PanelIntegrado';
import { MarcaLogo, TarjetaMonto, marcaDe } from './CatalogoBroadnet';
import {
  BnMarca, BnProductoFull, bnDenominacionEliminar, bnDenominacionGuardar, bnMarcaEliminar,
  bnMarcaGuardar, bnMarcasListar, bnProductoOtroMonto, fieldCls,
} from './shared';

// JPEG con fondo BLANCO: un logo PNG transparente pasado a JPEG queda negro.
async function imagenADataURL(file: File, max: number): Promise<string> {
  if (!file.type.startsWith('image/')) throw new Error('El archivo no es una imagen');
  const url = URL.createObjectURL(file);
  try {
    const img = await new Promise<HTMLImageElement>((ok, mal) => {
      const i = new Image(); i.onload = () => ok(i); i.onerror = () => mal(new Error('No se pudo leer la imagen')); i.src = url;
    });
    const k = Math.min(1, max / Math.max(img.width, img.height));
    const c = document.createElement('canvas');
    c.width = Math.max(1, Math.round(img.width * k)); c.height = Math.max(1, Math.round(img.height * k));
    const ctx = c.getContext('2d'); if (!ctx) throw new Error('Canvas no disponible');
    ctx.fillStyle = '#ffffff'; ctx.fillRect(0, 0, c.width, c.height);
    ctx.drawImage(img, 0, 0, c.width, c.height);
    return c.toDataURL('image/jpeg', 0.85);
  } finally { URL.revokeObjectURL(url); }
}

function SubirImagen({ onImagen, titulo }: { onImagen: (d: string) => void; titulo: string }) {
  return (
    <label className="inline-flex cursor-pointer items-center gap-1 rounded-md border px-2 py-1 text-xs hover:bg-muted" title={titulo}>
      <ImagePlus className="h-3.5 w-3.5" /> Imagen
      <input type="file" accept="image/*" className="hidden" onChange={async (e) => {
        const f = e.target.files?.[0]; e.target.value = '';
        if (!f) return;
        try { onImagen(await imagenADataURL(f, 480)); } catch (err) { toast.error((err as Error).message); }
      }} />
    </label>
  );
}

function Panel({ titulo, onClose, children }: { titulo: string; onClose: () => void; children: React.ReactNode }) {
  return <PanelIntegrado title={titulo} onClose={onClose} wide>{children}</PanelIntegrado>;
}

// ── Marcas ─────────────────────────────────────────────────────────────────
export function MarcasBroadnet({ onClose }: { onClose: () => void }) {
  const qc = useQueryClient();
  const dlg = useDialogoEnLinea();
  const { data: marcas = [] } = useQuery<BnMarca[]>({ queryKey: ['pos-bn-marcas'], queryFn: bnMarcasListar, staleTime: 0 });
  const refrescar = () => { void qc.invalidateQueries({ queryKey: ['pos-bn-marcas'] }); void qc.invalidateQueries({ queryKey: ['pos-bn-productos'] }); };
  const guardar = useMutation({
    mutationFn: (m: Record<string, unknown>) => bnMarcaGuardar(m),
    onSuccess: refrescar, onError: (e) => toast.error(getErrorMessage(e)),
  });
  const eliminar = useMutation({
    mutationFn: (id: string) => bnMarcaEliminar(id),
    onSuccess: (r) => { toast.success(`Marca eliminada${r.productos_sin_marca ? ` · ${r.productos_sin_marca} producto(s) quedaron sin marca` : ''}`); refrescar(); },
    onError: (e) => toast.error(getErrorMessage(e)),
  });
  const [nueva, setNueva] = useState('');

  return (
    <Panel titulo="Marcas del catálogo de recargas" onClose={onClose}>
      {dlg.ui}
      <p className="mb-3 text-xs text-muted-foreground">
        La marca agrupa los productos en el F4 del POS (p. ej. «MOVISTAR+TIGO» y sus combos bajo «Movistar»). Sin logo, se dibuja con sus iniciales y su color.
      </p>
      <div className="max-h-[60vh] space-y-2 overflow-y-auto pr-1">
        {marcas.map((m) => (
          <div key={m.id} className="flex flex-wrap items-center gap-2 rounded-lg border p-2">
            <MarcaLogo marca={m} size={40} />
            <input className={`${fieldCls} h-8 w-40`} defaultValue={m.nombre}
              onBlur={(e) => { if (e.target.value.trim() && e.target.value.trim() !== m.nombre) guardar.mutate({ id: m.id, nombre: e.target.value.trim() }); }} />
            <input type="color" className="h-8 w-10 cursor-pointer rounded border" value={m.color}
              onChange={(e) => guardar.mutate({ id: m.id, color: e.target.value })} title="Color de la marca" />
            <label className="flex items-center gap-1 text-xs text-muted-foreground">Orden
              <input type="number" className={`${fieldCls} h-8 w-16`} defaultValue={m.orden}
                onBlur={(e) => { const v = Number(e.target.value); if (v !== m.orden) guardar.mutate({ id: m.id, orden: v }); }} /></label>
            <SubirImagen titulo="Subir logo" onImagen={(d) => guardar.mutate({ id: m.id, logo: d })} />
            {m.logo && <button className="text-xs text-muted-foreground hover:underline" onClick={() => guardar.mutate({ id: m.id, logo: '' })}>quitar logo</button>}
            <span className="ml-auto text-xs text-muted-foreground">{m.productos ?? 0} producto(s)</span>
            <button className="rounded p-1 text-red-500 hover:bg-red-50 dark:hover:bg-red-900/20" title="Eliminar marca"
              onClick={async () => { if (await dlg.confirmar(`¿Eliminar la marca «${m.nombre}»?`, { detalle: 'Sus productos quedan sin marca.', peligro: true })) eliminar.mutate(m.id); }}>
              <Trash2 className="h-4 w-4" />
            </button>
          </div>
        ))}
      </div>
      <div className="mt-3 flex gap-2 border-t pt-3">
        <input className={`${fieldCls} h-9`} placeholder="Nueva marca…" value={nueva} onChange={(e) => setNueva(e.target.value)} />
        <Button disabled={!nueva.trim()} isLoading={guardar.isPending}
          onClick={() => guardar.mutate({ nombre: nueva.trim() }, { onSuccess: () => setNueva('') })}><Plus className="mr-1 h-4 w-4" /> Agregar</Button>
      </div>
    </Panel>
  );
}

// ── Montos / paquetes de un producto ────────────────────────────────────────
export function MontosBroadnet({ producto, onClose }: { producto: BnProductoFull; onClose: () => void }) {
  const qc = useQueryClient();
  const dlg = useDialogoEnLinea();
  const marca = marcaDe(producto);
  const denoms = producto.denominaciones ?? [];
  const refrescar = () => void qc.invalidateQueries({ queryKey: ['pos-bn-productos'] });
  const guardar = useMutation({
    mutationFn: (d: Record<string, unknown>) => bnDenominacionGuardar(d),
    onSuccess: refrescar, onError: (e) => toast.error(getErrorMessage(e)),
  });
  const eliminar = useMutation({
    mutationFn: (id: string) => bnDenominacionEliminar(id),
    onSuccess: refrescar, onError: (e) => toast.error(getErrorMessage(e)),
  });
  const [monto, setMonto] = useState('');
  const [etiqueta, setEtiqueta] = useState('');
  const otroMonto = useMutation({
    mutationFn: (v: boolean) => bnProductoOtroMonto(producto.id, v),
    onSuccess: refrescar, onError: (e) => toast.error(getErrorMessage(e)),
  });
  const permiteOtro = producto.permite_otro_monto !== false;

  return (
    <Panel titulo={`Montos · ${marca.nombre} · ${producto.servicio_nombre}`} onClose={onClose}>
      {dlg.ui}
      <p className="mb-3 text-xs text-muted-foreground">
        Cada monto es una tarjeta en el F4: el cajero la toca y no escribe la cantidad. Para paquetes/combos pon la descripción
        («5 GB · 7 días»). La imagen es opcional; sin ella la tarjeta se dibuja con el color de la marca.
      </p>
      {producto.monto_modo !== 'fijo' && (
        <label className="mb-3 flex items-start gap-2 rounded-md border px-3 py-2 text-sm">
          <input type="checkbox" className="mt-0.5" checked={permiteOtro} disabled={otroMonto.isPending}
            onChange={(e) => otroMonto.mutate(e.target.checked)} />
          <span>Mostrar la tarjeta <strong>«Otro monto»</strong> en el POS
            <span className="block text-xs text-muted-foreground">
              Apágala si el producto solo se vende por sus tarjetas (p. ej. combos: un monto tecleado no es ningún paquete).
              Necesita al menos una tarjeta activa.</span></span>
        </label>
      )}
      <div className="grid max-h-[55vh] grid-cols-2 gap-3 overflow-y-auto pr-1 sm:grid-cols-3">
        {denoms.map((d) => (
          <div key={d.id} className={`space-y-1.5 rounded-lg border p-2 ${d.es_activo ? '' : 'opacity-50'}`}>
            <TarjetaMonto marca={marca} monto={Number(d.monto)} etiqueta={d.etiqueta} imagen={d.imagen} onClick={() => undefined} />
            <div className="flex gap-1">
              <input type="number" step="0.01" min="0" className={`${fieldCls} h-8 w-20 text-right`} defaultValue={d.monto}
                onBlur={(e) => { const v = Number(e.target.value); if (v > 0 && v !== Number(d.monto)) guardar.mutate({ id: d.id, monto: v }); }} />
              <input className={`${fieldCls} h-8`} placeholder="Descripción" defaultValue={d.etiqueta ?? ''}
                onBlur={(e) => { if ((e.target.value.trim() || null) !== d.etiqueta) guardar.mutate({ id: d.id, etiqueta: e.target.value }); }} />
            </div>
            <div className="flex flex-wrap items-center gap-1.5">
              <SubirImagen titulo="Imagen de la tarjeta" onImagen={(img) => guardar.mutate({ id: d.id, imagen: img })} />
              {d.imagen && <button className="text-xs text-muted-foreground hover:underline" onClick={() => guardar.mutate({ id: d.id, imagen: '' })}>quitar</button>}
              <label className="ml-auto flex items-center gap-1 text-xs">
                <input type="checkbox" checked={d.es_activo} onChange={(e) => guardar.mutate({ id: d.id, es_activo: e.target.checked })} /> activo
              </label>
              <button className="rounded p-1 text-red-500 hover:bg-red-50 dark:hover:bg-red-900/20" title="Eliminar"
                onClick={async () => { if (await dlg.confirmar(`¿Eliminar el monto ${d.monto}?`, { peligro: true })) eliminar.mutate(d.id); }}><Trash2 className="h-3.5 w-3.5" /></button>
            </div>
          </div>
        ))}
        {!denoms.length && <p className="col-span-full py-4 text-center text-sm text-muted-foreground">Sin montos: en el POS solo aparece «Otro monto».</p>}
      </div>
      <div className="mt-3 flex flex-wrap items-end gap-2 border-t pt-3">
        <label className="text-xs text-muted-foreground">Monto $
          <input type="number" step="0.01" min="0" className={`${fieldCls} h-9 w-28 text-right`} value={monto} onChange={(e) => setMonto(e.target.value)} /></label>
        <label className="flex-1 text-xs text-muted-foreground">Descripción (opcional)
          <input className={`${fieldCls} h-9`} placeholder="p. ej. 5 GB · 7 días" value={etiqueta} onChange={(e) => setEtiqueta(e.target.value)} /></label>
        <Button disabled={!(Number(monto) > 0)} isLoading={guardar.isPending}
          onClick={() => guardar.mutate({ producto_id: producto.id, monto: Number(monto), etiqueta, orden: denoms.length + 1 },
            { onSuccess: () => { setMonto(''); setEtiqueta(''); } })}>
          <Plus className="mr-1 h-4 w-4" /> Agregar monto
        </Button>
      </div>
    </Panel>
  );
}
